import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import RoleService from "services/role.service";
import { toast } from "react-toastify";

export default function RoleForm() {
  const { name } = useParams<{ name: string }>();
  const history = useHistory();
  const isEditMode = Boolean(name);

  const [role, setRole] = useState({
    name: name || "ROLE_",
    permissions: [],
  });

  const [allPermissions, setAllPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  // État pour la validation
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    loadInitialData();
  }, [name]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const permsResponse = await RoleService.getAllPermissions();
      setAllPermissions(permsResponse.data);

      if (isEditMode) {
        const roleResponse = await RoleService.getById(name);
        setRole(roleResponse.data);
      }
    } catch (error) {
      toast.error("Erreur de chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    // Validation du nom : doit commencer par ROLE_ et avoir du texte après
    if (!role.name || role.name === "ROLE_") {
      newErrors.name = "Le nom du rôle est requis";
    } else if (!role.name.startsWith("ROLE_")) {
      newErrors.name = "Le nom doit commencer par ROLE_";
    }

    // Validation des permissions : au moins une requise
    if (role.permissions.length === 0) {
      newErrors.permissions = "Sélectionnez au moins une permission";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePermissionToggle = (permission) => {
    const isSelected = role.permissions.some((p) => p.id === permission.id);
    let updatedPermissions;

    if (isSelected) {
      updatedPermissions = role.permissions.filter(
        (p) => p.id !== permission.id,
      );
    } else {
      updatedPermissions = [...role.permissions, permission];
    }

    setRole({ ...role, permissions: updatedPermissions });

    // Effacer l'erreur de permission si on vient d'en ajouter une
    if (updatedPermissions.length > 0 && errors.permissions) {
      setErrors((prev) => ({ ...prev, permissions: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.warning("Veuillez corriger les erreurs avant de continuer");
      return;
    }

    try {
      setLoading(true);
      if (isEditMode) {
        await RoleService.update(role);
        toast.success("Rôle mis à jour avec succès");
      } else {
        await RoleService.create(role);
        toast.success("Rôle créé avec succès");
      }
      history.push("/admin/roles");
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center p-4 w-full min-h-screen">
      <div className="w-full max-w-2xl">
        <div className="relative flex flex-col bg-[#0B1222] border border-white/10 rounded-[1.5rem] shadow-2xl overflow-hidden">
          {/* HEADER */}
          <div className="bg-[#0F172A]/60 px-8 py-5 border-b border-white/5 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center border border-[#D4AF37]/20">
                <i
                  className={`fas ${isEditMode ? "fa-fingerprint" : "fa-shield-alt"} text-[#D4AF37] text-xs`}
                ></i>
              </div>
              <h2 className="text-white text-sm font-black uppercase tracking-[0.15em]">
                {isEditMode
                  ? `Modifier Rôle : ${name}`
                  : "Nouveau Rôle Système"}
              </h2>
            </div>
            <button
              onClick={() => history.goBack()}
              className="text-slate-500 hover:text-white transition-colors"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          <form className="p-8 lg:p-10" onSubmit={handleSubmit} noValidate>
            <div className="space-y-6">
              {/* NOM DU RÔLE */}
              <div className="space-y-2">
                <label className="text-[9px] text-[#D4AF37] uppercase font-black tracking-widest ml-1">
                  Nom technique de l'autorité
                </label>
                <input
                  type="text"
                  value={role.name}
                  onChange={(e) => {
                    setRole({ ...role, name: e.target.value.toUpperCase() });
                    if (errors.name)
                      setErrors((prev) => ({ ...prev, name: "" }));
                  }}
                  disabled={isEditMode}
                  className={`w-full bg-[#161F32]/50 border ${errors.name ? "border-red-500" : "border-white/5"} rounded-xl px-4 py-3 text-sm text-white focus:border-[#D4AF37] outline-none transition-all font-mono uppercase disabled:opacity-40`}
                  placeholder="ROLE_MODERATEUR"
                />
                {errors.name && (
                  <span className="text-red-500 text-[8px] font-bold uppercase ml-1 block">
                    {errors.name}
                  </span>
                )}
              </div>

              {/* SELECTION DES PERMISSIONS */}
              <div className="space-y-4">
                <div className="flex justify-between items-center ml-1">
                  <label className="text-[9px] text-[#D4AF37] uppercase font-black tracking-widest">
                    Assignation des permissions
                  </label>
                  {errors.permissions && (
                    <span className="text-red-500 text-[8px] font-bold uppercase">
                      {errors.permissions}
                    </span>
                  )}
                </div>

                <div
                  className={`grid grid-cols-1 md:grid-cols-2 gap-3 max-h-60 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-[#D4AF37]/20 p-1 rounded-xl ${errors.permissions ? "ring-1 ring-red-500/50" : ""}`}
                >
                  {allPermissions.map((permission) => {
                    const isChecked = role.permissions.some(
                      (p) => p.id === permission.id,
                    );
                    return (
                      <div
                        key={permission.id}
                        onClick={() => handlePermissionToggle(permission)}
                        className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                          isChecked
                            ? "bg-[#D4AF37]/5 border-[#D4AF37]/30 text-white"
                            : "bg-[#161F32]/30 border-white/5 text-slate-500 hover:border-white/10"
                        }`}
                      >
                        <div
                          className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${isChecked ? "bg-[#D4AF37] border-[#D4AF37]" : "border-white/20"}`}
                        >
                          {isChecked && (
                            <i className="fas fa-check text-[8px] text-[#0B1222]"></i>
                          )}
                        </div>
                        <span className="text-[11px] font-bold uppercase tracking-tight">
                          {permission.name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* INFO BOX */}
              <div className="bg-blue-500/5 border border-blue-500/10 rounded-xl p-4 flex gap-4 items-start">
                <i className="fas fa-info-circle text-blue-400 mt-1"></i>
                <p className="text-[10px] text-slate-400 leading-relaxed font-bold uppercase tracking-tight">
                  {isEditMode
                    ? "La modification des permissions d'un rôle affecte immédiatement tous les utilisateurs associés."
                    : "L'ajout d'un rôle permet de définir de nouveaux périmètres de sécurité."}
                </p>
              </div>

              {/* ACTIONS */}
              <div className="flex items-center justify-end pt-6 mt-4 border-t border-white/5 gap-4">
                <button
                  type="button"
                  onClick={() => history.goBack()}
                  className="px-6 py-2.5 text-[10px] font-black uppercase text-slate-500 hover:text-white transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-[#D4AF37] text-[#0B1222] px-10 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-[0.2em] shadow-lg shadow-[#D4AF37]/10 hover:scale-105 transition-all disabled:opacity-50"
                >
                  {loading
                    ? "Traitement..."
                    : isEditMode
                      ? "Mettre à jour"
                      : "Créer le rôle"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
